import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import type { ReactNode } from "react"
import { useAuth } from "@bw/shared/hooks"

const STORAGE_KEY = "bw_selected_organization"

interface OrganizationContextValue {
  organizationId: string | null
  selectOrganization: (organizationId: string) => void
  clearOrganization: () => void
}

const OrganizationContext = createContext<OrganizationContextValue>({
  organizationId: null,
  selectOrganization: () => {},
  clearOrganization: () => {},
})

function loadOrganizationId(storageKey: string | null): string | null {
  try {
    return storageKey ? localStorage.getItem(storageKey) : null
  } catch {
    return null
  }
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const accountId = user?.uid ?? null
  return <AccountOrganizationProvider key={JSON.stringify(accountId)} accountId={accountId}>
    {children}
  </AccountOrganizationProvider>
}

function AccountOrganizationProvider({ children, accountId }: { children: ReactNode; accountId: string | null }) {
  const storageKey = accountId ? `${STORAGE_KEY}:${encodeURIComponent(accountId)}` : null
  const [organizationId, setOrganizationId] = useState(() => loadOrganizationId(storageKey))

  useEffect(() => {
    // The old browser-wide value has no provable account owner. Never adopt it.
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* Storage is optional. */ }
  }, [])

  const selectOrganization = useCallback((nextOrganizationId: string) => {
    if (!storageKey) return
    setOrganizationId(nextOrganizationId)
    try {
      localStorage.setItem(storageKey, nextOrganizationId)
    } catch {
      // Storage can be unavailable; the in-memory selection remains authoritative.
    }
  }, [storageKey])

  const clearOrganization = useCallback(() => {
    setOrganizationId(null)
    try {
      if (storageKey) localStorage.removeItem(storageKey)
    } catch {
      // Ignore storage failures after clearing the in-memory selection.
    }
  }, [storageKey])

  const value = useMemo(
    () => ({ organizationId, selectOrganization, clearOrganization }),
    [organizationId, selectOrganization, clearOrganization],
  )

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export function useSelectedOrganization(): OrganizationContextValue {
  return useContext(OrganizationContext)
}
